/** @format */

import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    TextField,
    Alert,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Link as MuiLink,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { AppDispatch, RootState } from "../../../redux/store/store";
import {
    requestBusinessPasswordReset,
    confirmBusinessPasswordReset,
    clearResetPasswordState,
} from "../../../redux/slices/businessAuthSlice";
import { CollectPrimaryButton } from "../styles";
import { colors } from "../../../utility/types/constant";
import { BusinessAuthShell } from "./BusinessAuthShell";

/**
 * Self-service password reset for a Collect business user who is locked out.
 *
 * Two phases on a single screen (mirrors the circle reset experience but kept
 * self-contained for the business identity):
 *   1. Request — enter the work email; Cognito emails a one-time code.
 *   2. Reset   — enter that code + a new password to set it and return to login.
 */
const BusinessForgotPassword: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const {
        isRequestingReset,
        resetRequestError,
        resetCodeSent,
        isResettingPassword,
        resetPasswordError,
        pendingEmail,
    } = useSelector((s: RootState) => s.businessAuth);

    const [email, setEmail] = useState(pendingEmail || "");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);
    const [doneNote, setDoneNote] = useState<string | null>(null);

    // Start from a clean slate whenever the screen mounts, then clean up on exit
    // so a stale "code sent" flag never leaks into a later visit.
    useEffect(() => {
        dispatch(clearResetPasswordState());
        return () => {
            dispatch(clearResetPasswordState());
        };
    }, [dispatch]);

    const handleRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError(null);
        const trimmed = email.trim().toLowerCase();
        if (!trimmed) {
            setLocalError("Please enter your email.");
            return;
        }
        await dispatch(requestBusinessPasswordReset(trimmed));
    };

    const handleResend = async () => {
        setLocalError(null);
        await dispatch(requestBusinessPasswordReset(email.trim().toLowerCase()));
    };

    const handleReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError(null);

        if (!code.trim()) {
            setLocalError("Enter the verification code we emailed you.");
            return;
        }
        if (newPassword.length < 8) {
            setLocalError("New password must be at least 8 characters long.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setLocalError("Passwords do not match.");
            return;
        }

        const result = await dispatch(
            confirmBusinessPasswordReset({
                email: email.trim().toLowerCase(),
                code: code.trim(),
                newPassword,
            }),
        );

        if (confirmBusinessPasswordReset.fulfilled.match(result)) {
            setDoneNote(
                "Your password has been reset. Redirecting you to sign in…",
            );
            setTimeout(() => {
                navigate(result.payload.redirectTo || "/collect/sign-in");
            }, 1200);
        }
    };

    const signInFooter = (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Remembered it?{" "}
            <Link
                to="/collect/sign-in"
                style={{ color: colors.primary, fontWeight: 700 }}
            >
                Back to sign in
            </Link>
        </Typography>
    );

    // ── Phase 1: request a reset code ───────────────────────────────────────
    if (!resetCodeSent) {
        return (
            <BusinessAuthShell
                title="Forgot your password?"
                subtitle="Enter your work email and we'll send you a code to reset it."
                footer={signInFooter}
            >
                <form onSubmit={handleRequest} noValidate>
                    {(resetRequestError || localError) && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {resetRequestError || localError}
                        </Alert>
                    )}

                    <TextField
                        label="Work email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        required
                        size="small"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                    />

                    <CollectPrimaryButton
                        type="submit"
                        fullWidth
                        disabled={isRequestingReset}
                        sx={{ mt: 2 }}
                    >
                        {isRequestingReset ? (
                            <CircularProgress size={22} sx={{ color: "#fff" }} />
                        ) : (
                            "Send reset code"
                        )}
                    </CollectPrimaryButton>
                </form>
            </BusinessAuthShell>
        );
    }

    // ── Phase 2: confirm code + set new password ────────────────────────────
    return (
        <BusinessAuthShell
            title="Reset your password"
            subtitle="Enter the code we emailed you and choose a new password."
            footer={signInFooter}
        >
            <form onSubmit={handleReset} noValidate>
                {(resetPasswordError || localError) && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {resetPasswordError || localError}
                    </Alert>
                )}
                {doneNote && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {doneNote}
                    </Alert>
                )}

                <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                />

                <TextField
                    label="Verification code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ inputMode: "numeric", maxLength: 6 }}
                />

                <TextField
                    label="New password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                    helperText="At least 8 characters, with a mix of letters, numbers & symbols."
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowPassword((v) => !v)}
                                    edge="end"
                                >
                                    {showPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <TextField
                    label="Confirm new password"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                />

                <CollectPrimaryButton
                    type="submit"
                    fullWidth
                    disabled={isResettingPassword || Boolean(doneNote)}
                    sx={{ mt: 2 }}
                >
                    {isResettingPassword ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                    ) : (
                        "Reset password & sign in"
                    )}
                </CollectPrimaryButton>

                <Box sx={{ textAlign: "center", mt: 2 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Didn't get a code?{" "}
                        <MuiLink
                            component="button"
                            type="button"
                            onClick={handleResend}
                            disabled={isRequestingReset}
                            sx={{ fontWeight: 700 }}
                        >
                            Resend
                        </MuiLink>
                    </Typography>
                </Box>
            </form>
        </BusinessAuthShell>
    );
};

export default BusinessForgotPassword;
